import { useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight, Images } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { SEO } from "@/components/SEO";
import { PageHero, Loading } from "@/components/shared";

export default function Gallery() {
  const [albums, setAlbums] = useState(null);
  const [active, setActive] = useState("all");
  const [lightbox, setLightbox] = useState(null); // { photos, index }

  useEffect(() => { api.get("/public/albums").then((r) => setAlbums(r.data)); }, []);

  const categories = albums ? ["all", ...new Set(albums.map((a) => a.category).filter(Boolean))] : [];
  const shown = albums?.filter((a) => active === "all" || a.category === active) || [];

  const open = (photos, index) => setLightbox({ photos, index });
  const move = (d) => setLightbox((lb) => ({ ...lb, index: (lb.index + d + lb.photos.length) % lb.photos.length }));

  return (
    <>
      <SEO title="Gallery | Brackenfell Junior Rugby Club" description="Photos from BJRC training, matches, tournaments, team photos and club events." />
      <PageHero title="Gallery" subtitle="Moments from training, matches and club life." />
      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        {albums === null ? <Loading /> : albums.length === 0 ? (
          <div className="rounded-2xl bg-white p-16 text-center ring-1 ring-slate-200">
            <Images className="mx-auto h-12 w-12 text-slate-300" />
            <p className="mt-4 text-slate-500">No published photo albums yet. Please check back soon.</p>
          </div>
        ) : (
          <>
            {categories.length > 1 && (
              <div className="mb-8 flex flex-wrap gap-2">
                {categories.map((c) => (
                  <button key={c} onClick={() => setActive(c)} className={`rounded-full px-5 py-2 text-sm font-bold uppercase tracking-wide ${active === c ? "bg-[#0B2A4A] text-white" : "bg-white text-slate-600 ring-1 ring-slate-200"}`} data-testid={`gallery-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g,"-")}`}>
                    {c === "all" ? "All" : c}
                  </button>
                ))}
              </div>
            )}
            <div className="space-y-12">
              {shown.map((a) => (
                <div key={a.id} data-testid={`album-${a.id}`}>
                  <div className="flex items-end justify-between">
                    <div>
                      <h2 className="font-heading text-2xl font-extrabold uppercase text-[#0B2A4A]">{a.title}</h2>
                      {a.category && <span className="text-sm font-semibold text-[#1E63D6]">{a.category}</span>}
                    </div>
                  </div>
                  <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                    {(a.photos || []).map((p, i) => (
                      <button key={p.id} onClick={() => open(a.photos, i)} className="group aspect-square overflow-hidden rounded-xl bg-slate-200" data-testid={`photo-${p.id}`}>
                        <img src={mediaUrl(p.url)} alt={p.caption || a.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      {lightbox && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4" onClick={() => setLightbox(null)} data-testid="lightbox">
          <button className="absolute right-5 top-5 text-white" onClick={() => setLightbox(null)}><X className="h-8 w-8" /></button>
          <button className="absolute left-4 text-white" onClick={(e) => { e.stopPropagation(); move(-1); }}><ChevronLeft className="h-10 w-10" /></button>
          <figure onClick={(e) => e.stopPropagation()} className="max-h-[85vh] max-w-4xl text-center">
            <img src={mediaUrl(lightbox.photos[lightbox.index].url)} alt="" className="max-h-[80vh] rounded-lg object-contain" />
            {lightbox.photos[lightbox.index].caption && <figcaption className="mt-3 text-slate-300">{lightbox.photos[lightbox.index].caption}</figcaption>}
          </figure>
          <button className="absolute right-4 text-white" onClick={(e) => { e.stopPropagation(); move(1); }}><ChevronRight className="h-10 w-10" /></button>
        </div>
      )}
    </>
  );
}
